// SHADOW NETWORK — VideoCard
// Inline player rendered inside an AnalysisCard when media_type === "video".
// Click-to-play / pause, mute toggle, progress bar, badge with duration.
import React, { useRef, useState } from "react";

const fmt = (sec) => {
  if (!sec || sec <= 0) return "";
  const s = Math.round(sec);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

export const VideoCard = ({ videoUrl, thumbnailUrl, duration, postId }) => {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [muted, setMuted] = useState(true);

  const toggle = () => {
    const v = ref.current;
    if (!v) return;
    if (playing) {
      v.pause();
      setPlaying(false);
    } else {
      v.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    }
  };

  const onTime = () => {
    const v = ref.current;
    if (!v || !v.duration) return;
    setProgress((v.currentTime / v.duration) * 100);
  };

  return (
    <div
      onClick={toggle}
      className="relative aspect-[16/9] cursor-pointer overflow-hidden bg-black"
      data-testid={`shadow-card-video-${postId || ""}`}
    >
      <video
        ref={ref}
        src={videoUrl}
        poster={thumbnailUrl || undefined}
        muted={muted}
        playsInline
        preload="metadata"
        onTimeUpdate={onTime}
        onEnded={() => {
          setPlaying(false);
          setProgress(0);
        }}
        className="block h-full w-full object-cover"
      />

      {/* Play overlay (hides while playing) */}
      <div
        className="pointer-events-none absolute inset-0 flex items-center justify-center transition-opacity duration-200"
        style={{
          background: playing ? "transparent" : "rgba(0,0,0,0.3)",
          opacity: playing ? 0 : 1,
        }}
      >
        <div
          className="flex h-14 w-14 items-center justify-center rounded-full border-2 backdrop-blur-sm"
          style={{
            borderColor: "rgba(0,212,255,0.55)",
            background: "rgba(0,212,255,0.16)",
          }}
        >
          <span
            style={{
              color: "#00d4ff",
              fontSize: "1.4rem",
              marginLeft: 4,
              lineHeight: 1,
            }}
          >
            ▶
          </span>
        </div>
      </div>

      {/* Top-left badge: VIDEO · duration */}
      <span
        className="pointer-events-none absolute left-3 top-3 inline-flex items-center gap-1.5 rounded border bg-black/70 px-2 py-1 font-mono text-[10px] font-black uppercase tracking-[0.12em] backdrop-blur-sm"
        style={{ color: "#00d4ff", borderColor: "rgba(0,212,255,0.3)" }}
      >
        ▶ VÍDEO{duration ? ` · ${fmt(duration)}` : ""}
      </span>

      {/* Top-right: mute toggle */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setMuted((m) => !m);
        }}
        className="absolute right-3 top-3 flex h-7 w-7 items-center justify-center rounded-full border border-white/15 bg-black/65 text-[12px] text-white/75 backdrop-blur transition-colors hover:text-white"
        aria-label={muted ? "Activar sonido" : "Silenciar"}
        data-testid={`shadow-card-video-mute-${postId || ""}`}
      >
        {muted ? "🔇" : "🔊"}
      </button>

      {/* Bottom progress bar */}
      <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-white/10">
        <div
          className="h-full transition-[width] duration-100"
          style={{ width: `${progress}%`, background: "#00d4ff" }}
        />
      </div>
    </div>
  );
};

export default VideoCard;
